import { ProtectedRoute } from '@ophelos/ui/components/auth/protected-route';
import { Loader } from '@ophelos/ui/components/loader';
import { Toaster } from '@ophelos/ui/components/sonner';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FinanceShellPresentation } from './apps/finances/shell/FinanceShellPresentation';
import { FinanceAgentChatProvider } from './components/ai-chat/hooks/useFinanceAgentChat';
import { MarketOrganizationGuard } from './components/auth/MarketOrganizationGuard';
import { HubLayout } from './components/layout/HubLayout';
import { AppProtectedRoute } from './components/routes/AppProtectedRoute';
import { HubAuthorizedRoute } from './components/routes/HubAuthorizedRoute';
import { HubBootstrapGate } from './components/routes/HubBootstrapGate';
import { OperatorOnlyProtectedRoute } from './components/routes/OperatorOnlyProtectedRoute';
import { PermissionProtectedRoute } from './components/routes/PermissionProtectedRoute';
import { SystemAdminProtectedRoute } from './components/routes/SystemAdminProtectedRoute';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { AIChatProvider } from './contexts/AIChatContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { HubProvider } from './contexts/HubContext';
import { UserProfileProvider } from './hooks/useUserProfile';
import { initializeAuth } from './lib/auth';
import { CellProvider } from './lib/global/cell-context';
import { isGlobalSurface } from './lib/surface-mode';
import { AppsPage } from './pages/AppsPage';
import { CallbackPage } from './pages/auth/CallbackPage';
import { LoginPage } from './pages/auth/LoginPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { CustomerJourneyPage } from './pages/CustomerJourneyPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PresentationModePage } from './pages/PresentationModePage';
import { ProfilePage } from './pages/ProfilePage';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

// Lazy-loaded apps
const UsersApp = lazy(() => import('./apps/users'));
const CustomersApp = lazy(() => import('./apps/customers'));
const CampaignsApp = lazy(() => import('./apps/campaigns'));
const IngestionApp = lazy(() => import('./apps/ingestion'));
const AIApp = lazy(() => import('./apps/ai'));
const FinancesApp = lazy(() => import('./apps/finances'));
const LibraryApp = lazy(() => import('./apps/library'));
const AnalyticsApp = lazy(() => import('./apps/analytics'));
const SettingsApp = lazy(() => import('./apps/settings'));
const AdminApp = lazy(() => import('./apps/admin'));
const SupportApp = lazy(() => import('./apps/support'));
const ImportExportApp = lazy(() => import('./apps/import-export'));

// Module-level flag for auth initialization (prevents double-init in Strict Mode)
let didInitAuth = false;

const PUBLIC_LEGAL_PATHS = ['/cookie-policy'];

function AppContent() {
  const { isLoading, getAccessToken } = useAuth();
  const { pathname } = useLocation();

  if (!didInitAuth && !isLoading && getAccessToken) {
    initializeAuth(getAccessToken);
    didInitAuth = true;
  }

  // Legal pages are public and must render without waiting on the auth session.
  if (isLoading && !PUBLIC_LEGAL_PATHS.includes(pathname)) {
    return <Loader text="Completing sign in..." fullScreen />;
  }

  return (
    <Routes>
      {/* Public routes — outside all auth guards */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<CallbackPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />

      {/* All Hub routes require authentication + Hub authorization */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MarketOrganizationGuard>
              {isGlobalSurface() ? (
                <CellProvider>
                  <HubShell />
                </CellProvider>
              ) : (
                <HubShell />
              )}
            </MarketOrganizationGuard>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function HubShell() {
  return (
    <HubAuthorizedRoute>
      <UserProfileProvider>
        <HubProvider>
          <HubBootstrapGate>
            <AIChatProvider>
              <FinanceShellPresentation>
                <FinanceAgentChatProvider>
                  <Toaster position="top-right" closeButton />
                  <HubRoutes />
                </FinanceAgentChatProvider>
              </FinanceShellPresentation>
            </AIChatProvider>
          </HubBootstrapGate>
        </HubProvider>
      </UserProfileProvider>
    </HubAuthorizedRoute>
  );
}

/** apps/files was renamed to apps/ingestion; preserve old bookmarks/links by redirecting the whole subtree. */
function RedirectToIngestionApp() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^(\/[^/]+)?\/app\/files/, '');
  const operatorId = location.pathname.match(/^\/([^/]+)\/app\/files/)?.[1];
  const prefix = operatorId && operatorId !== 'app' ? `/${operatorId}` : '';
  return <Navigate to={`${prefix}/app/ingestion${suffix}${location.search}${location.hash}`} replace />;
}

function HubRoutes() {
  const p = isGlobalSurface() ? '/:operatorId' : '';
  return (
    <Routes>
      {/* Apps with compact layout (outside HubLayout) */}
      <Route
        path={`${p}/app/customers/*`}
        element={
          <AppProtectedRoute module="customer-care" appName="customers">
            <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
              <CustomersApp />
            </Suspense>
          </AppProtectedRoute>
        }
      />
      <Route path={`${p}/app/files/*`} element={<RedirectToIngestionApp />} />

      {/* Playground pages with custom layouts (outside HubLayout) */}
      <Route path={`${p}/playground/customer-journey`} element={<CustomerJourneyPage />} />

      {/* Main hub routes */}
      <Route path={p || '/'} element={<HubLayout />}>
        <Route index element={<Navigate to="apps" replace />} />
        <Route path="apps" element={<AppsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route
          path="presentation"
          element={
            <PermissionProtectedRoute module="analytics" description="You need analytics admin permissions to access this page.">
              <PresentationModePage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="users/*"
          element={
            /*
              `module="users"` is `users:admin` or the legacy `app:users:access` —
              the same gate the sidebar uses, so the page and the link agree.
              It was briefly `permission="users:read"`, which also named
              `customer-care-*` agents: they could open the roster and every member
              page, and (because the grant read is `users:admin`) see None for
              everyone. Settings Viewer and Settings Admin both still pass.
            */
            <PermissionProtectedRoute module="users" description="You need permission to view the user directory.">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <UsersApp />
              </Suspense>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="settings/*"
          element={
            <PermissionProtectedRoute module="config" description="You need settings permissions to access this page.">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <SettingsApp />
              </Suspense>
            </PermissionProtectedRoute>
          }
        />

        {/* Operations apps */}
        <Route
          path="app/campaigns/*"
          element={
            <AppProtectedRoute module="campaigns" appName="campaigns">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <CampaignsApp />
              </Suspense>
            </AppProtectedRoute>
          }
        />
        <Route
          path="app/ingestion/*"
          element={
            <AppProtectedRoute module="ingestion" appName="ingestion">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <IngestionApp />
              </Suspense>
            </AppProtectedRoute>
          }
        />
        <Route
          path="app/library/*"
          element={
            <AppProtectedRoute module="library" appName="library">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <LibraryApp />
              </Suspense>
            </AppProtectedRoute>
          }
        />

        {/* Configuration apps */}
        <Route
          path="app/import-export/*"
          element={
            <AppProtectedRoute module="config" appName="import-export">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <ImportExportApp />
              </Suspense>
            </AppProtectedRoute>
          }
        />
        <Route
          path="app/ai/*"
          element={
            <AppProtectedRoute module="ai-agents" appName="ai">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <AIApp />
              </Suspense>
            </AppProtectedRoute>
          }
        />

        {/* Evaluation apps */}
        <Route
          path="app/finances/*"
          element={
            <AppProtectedRoute module="finances" appName="finances">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <FinancesApp />
              </Suspense>
            </AppProtectedRoute>
          }
        />
        <Route
          path="app/analytics/*"
          element={
            <AppProtectedRoute module="analytics" appName="analytics">
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <AnalyticsApp />
              </Suspense>
            </AppProtectedRoute>
          }
        />

        {/* Support app - operator-only (role-based, no app permission) */}
        <Route
          path="app/support/*"
          element={
            <OperatorOnlyProtectedRoute>
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <SupportApp />
              </Suspense>
            </OperatorOnlyProtectedRoute>
          }
        />

        {/* System admin apps */}
        <Route
          path="app/admin/*"
          element={
            <SystemAdminProtectedRoute>
              <Suspense fallback={<Loader text="Loading..." size="lg" fullScreen />}>
                <AdminApp />
              </Suspense>
            </SystemAdminProtectedRoute>
          }
        />
        {/* 404 catch-all route for routes within HubLayout */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* 404 catch-all route for routes outside HubLayout */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ThemeProvider defaultTheme="light" storageKey="ophelos-hub-theme">
          <CookieConsentProvider>
            <AppContent />
          </CookieConsentProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
